import { __read } from "tslib";
import React from 'react';
/**
 * Creates a collection of event handlers to notify when the window resizes and
 * either hides or reveals an item at a specific index on the AppBar
 * @returns MediaQueryListener hook
 */
export var useDynamicMediaQueryListener = function (matchMedia) {
    var _a = __read(React.useState(0), 2), bottomPadding = _a[0], setBottomPadding = _a[1];
    var _b = __read(React.useState(0), 2), topMargin = _b[0], setTopMargin = _b[1];
    var _c = __read(React.useState(0), 2), elementHeight = _c[0], setElementHeight = _c[1];
    var _d = __read(React.useState(), 2), elementCount = _d[0], setElementCount = _d[1];
    var _e = __read(React.useState(0), 2), minimumVisibleElementCount = _e[0], setMinimumVisibleElementCount = _e[1];
    var _f = __read(React.useState([]), 2), visibilityVector = _f[0], setVisibilityVector = _f[1];
    /**
     * Creates a query collection containing each elements media query
     */
    var queryList = React.useMemo(function () {
        if (elementCount === undefined) {
            return undefined;
        }
        var queries = [];
        for (var i = 0; i < elementCount; i++) {
            var threshold = elementHeight * (i + 1) + bottomPadding + topMargin;
            var query = "(max-height: ".concat(threshold, "px)");
            queries.push(query);
        }
        return queries;
    }, [bottomPadding, elementCount, elementHeight, topMargin]);
    /**
     * Builds the visibility vector based on queries within the queryList
     */
    var buildVisibilityVector = React.useCallback(function () {
        if (queryList === undefined) {
            return;
        }
        var vector = queryList.map(function (mediaQuery) {
            return !matchMedia(mediaQuery).matches;
        });
        vector = vector.fill(true, 0, minimumVisibleElementCount);
        if (vector.length > 0 && visibilityVector.join() !== vector.join()) {
            setVisibilityVector(vector);
        }
    }, [matchMedia, minimumVisibleElementCount, queryList, visibilityVector]);
    /**
     * Creates an event listener for each query in queryList to rebuild
     * the visibility vector when the window resizes
     * @remarks mediaQuery.addEventListener is mediaQuery.addListener for Safari 13 and below
     */
    React.useEffect(function () {
        var mediaQueries = [];
        queryList === null || queryList === void 0 ? void 0 : queryList.forEach(function (query) {
            var mediaQuery = matchMedia(query);
            try {
                mediaQuery.onchange = buildVisibilityVector;
            }
            catch (_a) {
                try {
                    mediaQuery.addListener(buildVisibilityVector);
                }
                catch (_b) { }
            }
            mediaQueries.push(mediaQuery);
        });
        buildVisibilityVector();
        return function () {
            mediaQueries.forEach(function (mediaQuery) {
                try {
                    mediaQuery.onchange = null;
                }
                catch (_a) {
                    try {
                        mediaQuery.removeListener(buildVisibilityVector);
                    }
                    catch (_b) { }
                }
            });
            mediaQueries.splice(0);
        };
    }, [matchMedia, buildVisibilityVector, queryList]);
    return {
        setBottomPadding: setBottomPadding,
        setTopMargin: setTopMargin,
        setElementHeight: setElementHeight,
        setElementCount: setElementCount,
        setMinimumVisibleElementCount: setMinimumVisibleElementCount,
        visibilityVector: visibilityVector,
    };
};
//# sourceMappingURL=useMediaQueryListener.js.map